/* global BigInt */
import * as money from './costMath';
import { integratePowerSamples } from './energyMath';
import { coverage, withLineage } from './lineage';
import { counterReadings } from './counterPaths';

const time = (value) => value == null ? NaN : Date.parse(value);
const sequence = (row) => BigInt(row.sequenceNumber || '0');
const exactText = value => { const number = money.decimal(value); return money.fixed(number, Math.max(number.s, 9)).replace(/\.?0+$/, '') || '0'; };
const physicalKey = (row) => `${row.deviceId}:${row.equipmentId || '?'}`;

export function countersFromMessage(row) {
  return counterReadings(row.messageType, row.payload || {}).map((item) => ({
    ...item, value: exactText(money.fixed(String(item.currentValue), 9)), deviceId: row.deviceId, siteId: row.siteId,
    fleetId: row.fleetId, canopyId: row.canopyId, observedAt: row.observedAt,
    sourceEnergyMessageId: row.energyMessageId, sequenceNumber: row.sequenceNumber,
  }));
}

export function counterEnergy(observations, events, start, end, context = {}) {
  const startMs = time(start); const endMs = time(end);
  const expected = Math.max(0, (endMs - startMs) / 1000);
  const reasons = new Set();
  const valid = observations.filter(row => {
    const knownTime = Number.isFinite(time(row.sampleTime));
    if (!knownTime) reasons.add('unknownCounterAcquisitionTime');
    const status = row.dataStatus === undefined || row.dataStatus === null || row.dataStatus === 'available';
    if (!status) reasons.add('counterAcquisitionUnavailable');
    try { money.decimal(row.value); } catch { reasons.add('counterValueUnavailable'); return false; }
    return knownTime && status && time(row.sampleTime) >= startMs && time(row.sampleTime) <= endMs;
  });
  const groups = new Map();
  const physicalRanges = new Map();
  valid.forEach(row => {
    const key = physicalKey(row);
    if (!physicalRanges.has(row.deviceId)) physicalRanges.set(row.deviceId, new Map());
    const identities = physicalRanges.get(row.deviceId);
    const span = identities.get(row.equipmentId) || { min: Infinity, max: -Infinity };
    span.min = Math.min(span.min, time(row.sampleTime)); span.max = Math.max(span.max, time(row.sampleTime));
    identities.set(row.equipmentId, span);
    if (!groups.has(key)) groups.set(key, new Map());
    const series = groups.get(key); const at = time(row.sampleTime); const prior = series.get(at);
    if (!prior || time(row.observedAt) > time(prior.observedAt)
      || (time(row.observedAt) === time(prior.observedAt) && sequence(row) > sequence(prior))) series.set(at, row);
  });
  const segments = []; const excluded = [];
  for (const series of groups.values()) {
    const rows = [...series.values()].sort((a, b) => time(a.sampleTime) - time(b.sampleTime));
    for (let index = 1; index < rows.length; index += 1) {
      const previous = rows[index - 1]; const current = rows[index];
      const a = time(previous.sampleTime); const b = time(current.sampleTime);
      const discontinuities = events.filter(event => event.deviceId === current.deviceId
        && (!event.metric || event.metric === current.metric)
        && time(event.currentObservedAt || event.observedAt || event.sampleTime) > a && time(event.currentObservedAt || event.observedAt || event.sampleTime) <= b);
      let reason = null;
      if (!current.equipmentId || !previous.equipmentId) reason = 'counterPhysicalIdentityUnavailable';
      else if (discontinuities.length) reason = discontinuities.some(event => event.eventType === 'rollover')
        ? 'counterRolloverUnresolved' : 'counterDiscontinuity';
      const delta = money.subtract(current.value, previous.value);
      if (money.compare(delta, '0') < 0) reason = reason || 'counterReset';
      const overlapsIdentity = [...physicalRanges.get(current.deviceId)].some(([equipmentId, span]) =>
        equipmentId !== current.equipmentId && span.min <= b && span.max >= a);
      if (overlapsIdentity) reason = reason || 'counterDiscontinuity';
      if (reason) { reasons.add(reason); excluded.push({ start: previous.sampleTime, end: current.sampleTime, reason }); }
      else segments.push({ start: previous.sampleTime, end: current.sampleTime, seconds: (b - a) / 1000,
        value: exactText(delta), physicalKey: physicalKey(current) });
    }
  }
  segments.sort((a, b) => time(a.start) - time(b.start));
  const accepted = []; let through = -Infinity;
  for (const segment of segments) {
    if (time(segment.start) < through) { reasons.add('counterOverlappingPhysicalSeries'); continue; }
    accepted.push(segment); through = time(segment.end);
  }
  const covered = accepted.reduce((sum, item) => sum + item.seconds, 0);
  const evidence = coverage(expected, covered);
  const firstMs = valid.length ? valid.reduce((min, row) => Math.min(min, time(row.sampleTime)), Infinity) : endMs;
  const lastMs = valid.length ? valid.reduce((max, row) => Math.max(max, time(row.sampleTime)), -Infinity) : startMs;
  const boundarySeconds = valid.length ? Math.max((firstMs - startMs) / 1000, 0) + Math.max((endMs - lastMs) / 1000, 0) : expected;
  if (boundarySeconds > 0) { reasons.add('unallocatedBoundarySpan'); reasons.add('exactBoundaryCounterReadingsUnavailable'); }
  if (!accepted.length && valid.length < 2) reasons.add('insufficientCompatibleCounterEndpoints');
  const complete = accepted.length > 0 && covered === expected && firstMs === startMs && lastMs === endMs && excluded.length === 0;
  return withLineage({
    value: accepted.length ? exactText(accepted.reduce((sum, item) => money.add(sum, item.value), money.decimal('0'))) : null,
    sourceWatermark: { sourceCount: valid.length, deviceIds: [...physicalRanges.keys()],
      firstSampleTime: valid.length ? new Date(firstMs).toISOString() : null, lastSampleTime: valid.length ? new Date(lastMs).toISOString() : null,
      lastObservedAt: valid.reduce((last, row) => time(row.observedAt) > time(last) || last == null ? row.observedAt : last, null),
      lastEnergyMessageId: valid.reduce((last, row) => BigInt(row.sourceEnergyMessageId || row.energyMessageId || 0) > BigInt(last) ? String(row.sourceEnergyMessageId || row.energyMessageId) : last, '0') },
    unit: 'kWh', status: complete ? 'complete' : accepted.length ? 'partial' : 'unavailable',
    reasonCodes: [...reasons], sourceKind: 'counter_derived', coverage: evidence,
    allocatedStart: accepted[0]?.start || null, allocatedEnd: accepted.at(-1)?.end || null,
    unallocatedBoundarySeconds: boundarySeconds, excludedSegmentSeconds: Math.max(evidence.missingSeconds - boundarySeconds, 0),
    segments: accepted, gaps: excluded,
  }, { ...context, cutoffTime: end });
}

export function estimatedEnergy(samples, start, end, direction = 'positive', context = {}) {
  const estimate = integratePowerSamples(samples.filter(row => row.sampleTime != null && typeof row.kw === 'number'), start, end);
  return withLineage({ ...estimate, value: estimate.coveredSeconds > 0 ? estimate[`${direction}EnergyKwh`] : null, unit: 'kWh',
    sourceKind: 'estimated', coverage: coverage(estimate.expectedSeconds, estimate.coveredSeconds) }, { ...context, cutoffTime: end });
}

// Retain original witnesses after verifying the intervening monotone chain.
// Each exact report boundary, grid crossing and discontinuity keeps both adjacent
// readings. The newest pair remains available for the next live comparison.
export function compactCounterWitnesses(rows, events, windows, configuration = {}) {
  const groups = new Map(); const unknown = new Map();
  for (const row of rows) {
    const key = `${row.metric}:${physicalKey(row)}`;
    if (!Number.isFinite(time(row.sampleTime)) || (row.dataStatus != null && row.dataStatus !== 'available')) { unknown.set(key, row); continue; }
    try { money.decimal(row.value); } catch { unknown.set(key, row); continue; }
    if (!groups.has(key)) groups.set(key, new Map());
    const series = groups.get(key); const at = time(row.sampleTime); const old = series.get(at);
    if (!old || time(row.observedAt) > time(old.observedAt) || (time(row.observedAt) === time(old.observedAt) && sequence(row) > sequence(old))) series.set(at, row);
  }
  const boundaries = ['selected', 'today', 'billing', 'lease'].flatMap(key => [time(windows[key].start), time(windows[key].end)]);
  const anchor = time(windows.billing.start);
  const gcd = (a, b) => b ? gcd(b, a % b) : a;
  const result = [...unknown.values()];
  for (const series of groups.values()) {
    const ordered = [...series.values()].sort((a, b) => time(a.sampleTime) - time(b.sampleTime));
    const keep = new Set([0, ordered.length - 1, ordered.length - 2]);
    for (let index = 1; index < ordered.length; index += 1) {
      const previous = ordered[index - 1]; const current = ordered[index];
      const a = time(previous.sampleTime); const b = time(current.sampleTime);
      const step = ['pcc.imported.lifetime', 'load.served.lifetime'].includes(current.metric) ? gcd(configuration.demandIntervalMinutes || 15, 60) * 60000 : 1800000;
      const discontinuity = events.some(event => event.deviceId === current.deviceId && (!event.metric || event.metric === current.metric)
        && time(event.currentObservedAt || event.observedAt || event.sampleTime) > a && time(event.currentObservedAt || event.observedAt || event.sampleTime) <= b);
      if (money.compare(current.value, previous.value) < 0 || discontinuity || boundaries.some(boundary => boundary > a && boundary <= b)
        || Math.floor((b - anchor) / step) !== Math.floor((a - anchor) / step)) { keep.add(index); keep.add(index - 1); }
    }
    for (const index of [...keep].filter(index => index >= 0).sort((a, b) => a - b)) result.push(ordered[index]);
  }
  return result;
}
